import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import { COLORS, SIZES } from '../../styles/theme';
import CircularProgressBar from './CircularProgressBar';

const StyledDialogTitle = styled(DialogTitle)(() => ({
  padding: '20px 24px 5px',
}));

const StyledButtonPrimary = styled(Button)(() => ({
  color: COLORS.WHITE,
  backgroundColor: COLORS.PRIMARY_BLUE,
  minWidth: SIZES.MIN_BUTTON_WIDTH,
  float: 'right',
  '&:hover': {
    backgroundColor: COLORS.PRIMARY_BLUE,
  },
}));

const StyledDialogActions = styled(DialogActions)(() => ({
  padding: '8px 35px 20px 8px',
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  padding: '8px 11px 8px 22px',
}));

const StyledDialogTitleText = styled('div')(() => ({
  fontWeight: 'bold',
}));

export interface DialogDataProps {
  show: boolean;
  text: string;
}

export interface CommonDialogProps {
  children: React.ReactNode;
  customstyle?: React.ElementType;
  id: string;
  keepMounted: boolean;
  open: boolean;
  onDismiss?: () => void;
  onConfirm: (value?: boolean) => Promise<boolean>;
  title: DialogDataProps;
  btnCancel: DialogDataProps;
  btnOk: DialogDataProps;
  maxWidth: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showErrorTooltipMessgae?: any;
}

export const CommonDialog = (props: CommonDialogProps) => {
  const {
    onDismiss,
    onConfirm,
    open,
    children,
    id,
    title,
    btnCancel,
    btnOk,
    maxWidth,
    showErrorTooltipMessgae,
    customstyle,
    ...other
  } = props;
  const [showProgress, setShowProgress] = useState<boolean>(false);

  const handleOk = async () => {
    setShowProgress(true);
    const res = await onConfirm(true);
    setShowProgress(!res);
  };
  const handleSubmit = (event: any) => {
    event.preventDefault();
    handleOk();
  };

  return (
    <Dialog
      maxWidth={maxWidth}
      aria-labelledby={`${id}-title`}
      open={open}
      {...(customstyle ? { customstyle } : {})}
      {...other}
    >
      <form onSubmit={handleSubmit}>
        {title.show && (
          <StyledDialogTitle id={`${id}-title`}>
            <StyledDialogTitleText>{title.text}</StyledDialogTitleText>
          </StyledDialogTitle>
        )}
        <StyledDialogContent>{children}</StyledDialogContent>
        <StyledDialogActions>
          {btnCancel.show && (
            <StyledButtonPrimary
              onClick={onDismiss}
              color="primary"
              data-testid="cancelButton"
            >
              {btnCancel.text}
            </StyledButtonPrimary>
          )}
          {btnOk.show && (
            <Tooltip
              title={showErrorTooltipMessgae ? showErrorTooltipMessgae : ''}
              arrow
              placement="top"
            >
              <span>
                <StyledButtonPrimary
                  onClick={handleOk}
                  color="primary"
                  data-testid="okButton"
                >
                  {btnOk.text}
                </StyledButtonPrimary>
              </span>
            </Tooltip>
          )}
          {showProgress && (
            <div className="MuiProgressOverlay">
              <CircularProgressBar />
            </div>
          )}
        </StyledDialogActions>
      </form>
    </Dialog>
  );
};
